<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section
    class="sectionContainer mb-10 lg:min-h-full lg:mb-20"
    ref="scrollElement"
  >
    <article
      class="col-start-2 col-span-12 mt-16 md:col-span-8 md:col-start-2 md:grid md:grid-cols-8 md:gap-x-12 md:mt-24 2xl:gap-x-20"
    >
      <paragraph
        v-if="partners"
        classes="md:col-span-6 font-bold mb-6 md:mb-8"
        :is-umbraco="isUmbraco"
        :theme="themeType"
        :text="partners"
      />
      <header1
        v-if="title"
        :text="title"
        classes="mb-10 md:col-span-8 lg:col-span-6 md:mb-5"
        :is-umbraco="isUmbraco"
        :theme="themeType"
      />
    </article>
    <div
      v-if="imageJson"
      class="col-start-1 col-span-full w-full h-auto lg:h-[80vh]"
    >
      <image-component
        :image="imageJson"
        :img-alt="imageAltText"
        classes="w-full h-full object-cover"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import Header1 from "@components/Typography/Header1.ce.vue";
import Paragraph from "@components/Typography/Paragraph.ce.vue";

import ImageComponent from "@atoms/Image/Img.vue";
import { changeTheme, getThemeType } from "@/util/helper";
import { Ref, computed, onMounted, onUnmounted, ref, toRefs, watch } from "vue";
import { IImage, Theme } from "@/types/types";
import { onIntersect } from "@/composables/onIntersect";

export interface ProjectHeroSectionProps {
  isUmbraco?: boolean;
  title?: string;
  image?: IImage;
  imageAltText?: string;
  theme?: Theme;
  partners?: string;
}

const props = withDefaults(defineProps<ProjectHeroSectionProps>(), {
  isUmbraco: false,
  imageAltText: "",
  theme: "sand" as Theme,
});

const { title, image, isUmbraco, theme } = toRefs(props);

const observer: Ref<IntersectionObserver | null> = ref(null);
const scrollElement = ref(null);

const onEnter = () => {
  if (theme?.value) {
    changeTheme(theme.value);
  }
};
const onExit = () => {};

onMounted(() => {
  observer.value = onIntersect(scrollElement.value, onEnter, onExit, false, {
    threshold: 0.6,
  });
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const fontColor = computed(() => {
  return isUmbraco.value && themeType.value === "dark"
    ? "text-white border-white-40"
    : isUmbraco.value && themeType.value === "light"
    ? "text-aubergine border-aubergine-40"
    : "text-typography border-typography-40";
});

const tagStyle = computed(() => {
  return `${fontColor.value} self-start min-h-[30px] inline-block
  border-2 border-solid
  p-3 lg:px-5 lg:py-3
          mb-8 md:mb-0 
          md:mr-10`;
});

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const imageJson = computed(() => {
  if (!image?.value) return;
  return JSON.stringify(image.value);
});
</script>
